import React, { useMemo } from "react";
import styled, { css } from "styled-components";
import { Alert } from "components/bootstrap";
import { Spinner, Timestamp } from "components/common";
import { useGetPriorities } from "security-app/hooks/useInvestigationsAPI";
import InvestigationsLabel from "security-app/components/common/InvestigationsLabel";
import PaperSection from "security-app/components/Investigations/AIAgentTriageInvestigation/common/PaperSection";
import TriageStatus from "security-app/components/Investigations/AIAgentTriageInvestigation/common/TriageStatus";
import EventSection from "./EventSection";
import useAIAgentTriageInvestigationOutput from "./useAIAgentTriageInvestigationOutput";
const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.md};
  `
);
const SummaryGrid = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
    gap: ${theme.spacings.md};

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  `
);
const TextBlock = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xs};

    p {
      margin: 0;
    }
  `
);
const Counts = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacings.xs};
  `
);
const EventList = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.sm};
  `
);
const EventAnalysis = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.sm};

    h2 {
      margin: 0;
      font-size: ${theme.fonts.size.h2};
    }
  `
);
const EventStatusCounts = ({ counts }) => /* @__PURE__ */ React.createElement(Counts, null, /* @__PURE__ */ React.createElement(TriageStatus, { count: counts.evaluated, status: "EVALUATED" }), /* @__PURE__ */ React.createElement(TriageStatus, { count: counts.confirmed, status: "CONFIRMED" }), /* @__PURE__ */ React.createElement(TriageStatus, { count: counts.suppressed, status: "SUPPRESSED" }), /* @__PURE__ */ React.createElement(TriageStatus, { count: counts.escalated, status: "ESCALATED" }));
const AIAgentTriageInvestigation = ({ investigationId }) => {
  var _a;
  const { data, isLoading, isError, error } = useAIAgentTriageInvestigationOutput(investigationId);
  const { loadingPriorities, priorities } = useGetPriorities(true);
  const recommendedPriority = (_a = data == null ? void 0 : data.recommended_priority) == null ? void 0 : _a.priority;
  const recommendedPriorityText = useMemo(
    () => {
      var _a2;
      return (_a2 = priorities.find((priority) => priority.priority === recommendedPriority)) == null ? void 0 : _a2.text;
    },
    [recommendedPriority, priorities]
  );
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, { text: "Loading AI agent triage..." });
  }
  if (isError) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, error instanceof Error ? error.message : "Unable to load AI agent triage.");
  }
  if (!data) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(
    PaperSection,
    {
      title: "Agent Triage",
      header: "Agent Triage",
      headerLeftSection: /* @__PURE__ */ React.createElement(EventStatusCounts, { counts: data.event_status_counts }),
      actions: /* @__PURE__ */ React.createElement(Timestamp, { dateTime: data.last_run_at })
    }
  ), /* @__PURE__ */ React.createElement(SummaryGrid, null, /* @__PURE__ */ React.createElement(PaperSection, { title: "Agent Initial Assessment" }, /* @__PURE__ */ React.createElement(TextBlock, null, /* @__PURE__ */ React.createElement("p", null, data.agent_initial_assessment.summary), /* @__PURE__ */ React.createElement("p", null, data.agent_initial_assessment.recommendation))), /* @__PURE__ */ React.createElement(PaperSection, { title: "Recommended Priority" }, /* @__PURE__ */ React.createElement(TextBlock, null, /* @__PURE__ */ React.createElement("p", null, loadingPriorities ? /* @__PURE__ */ React.createElement(Spinner, { text: "Loading priorities..." }) : /* @__PURE__ */ React.createElement(InvestigationsLabel, { bsStyle: "default" }, recommendedPriorityText != null ? recommendedPriorityText : `Priority ${data.recommended_priority.priority}`)), /* @__PURE__ */ React.createElement("p", null, data.recommended_priority.explanation)))), /* @__PURE__ */ React.createElement(EventAnalysis, null, /* @__PURE__ */ React.createElement("h2", null, "Events - Agent Analysis"), /* @__PURE__ */ React.createElement(EventList, null, data.events.map((event) => /* @__PURE__ */ React.createElement(EventSection, { key: event.security_event_id, event })))));
};
export default AIAgentTriageInvestigation;
